import type { TargetRenderer } from "./target-renderer";

export interface AdRefImageHints {
  /** Forwarded to imgproxy as `w:<n>` via the `width` query param. */
  width?: number;
  /** Forwarded to imgproxy as `h:<n>` via the `height` query param. */
  height?: number;
  /** Target image format. Defaults to source format when omitted. */
  format?: "webp" | "jpg" | "png";
  targetRenderer: TargetRenderer;
  /**
   * Signed URLs for the rendition these hints describe, keyed by `ad:`
   * filename. A filename present here resolves straight to its URL instead of
   * a `/agents/files` URL, skipping the API's authenticated redirect; absent
   * filenames still go through the API.
   */
  directUrls?: Readonly<Record<string, string>>;
}

/**
 * Set width/height/format query params on an existing `/agents/files` URL.
 * Leaves URLs that don't point at the agent-files endpoint, can't be parsed,
 * or are missing a `key` param unchanged. Hints override pre-existing same-
 * named params so that an explicit caller intent always wins.
 */
export function injectHintsIntoAgentFileUrl(
  url: string,
  hints: AdRefImageHints,
): string {
  const parsed = parseAgentFileUrl(url);
  if (!parsed) return url;
  if (hints.width) parsed.searchParams.set("width", String(hints.width));
  if (hints.height) parsed.searchParams.set("height", String(hints.height));
  if (hints.format) parsed.searchParams.set("format", hints.format);
  if (hints.targetRenderer === "figma") {
    parsed.searchParams.set("targetRenderer", "figma");
  }
  return parsed.toString();
}

/**
 * Inverse of `injectHintsIntoAgentFileUrl`: strip the imgproxy resize/convert
 * params (`width`/`height`/`format`) so the URL resolves to the raw stored
 * object via the API's redirect. imgproxy rejects sources above its max source
 * resolution (e.g. a brand logo exported at 16384×3640 ≈ 60 MP), so every
 * resized request fails even though the raw object loads fine in an `<img>`.
 *
 * For `.svg` keys it sets `stream=true` so the API serves `image/svg+xml`;
 * browsers won't paint a raw `application/octet-stream` SVG.
 *
 * Returns null when the URL isn't an agent-files URL or carries no resize
 * params (it's already raw — nothing to fall back to). Callers use null to
 * stop retrying, avoiding an infinite error loop.
 */
export function rawAgentFileUrlFallback(url: string): string | null {
  const parsed = parseAgentFileUrl(url);
  if (!parsed) return null;
  const hadResizeParams =
    parsed.searchParams.has("width") ||
    parsed.searchParams.has("height") ||
    parsed.searchParams.has("format");
  if (!hadResizeParams) return null;
  parsed.searchParams.delete("width");
  parsed.searchParams.delete("height");
  parsed.searchParams.delete("format");
  const key = parsed.searchParams.get("key") ?? "";
  if (key.toLowerCase().endsWith(".svg")) {
    parsed.searchParams.set("stream", "true");
  }
  return parsed.toString();
}

/**
 * The raw-object `/agents/files` URL for an `ad:filename` ref, for an `<img>`
 * whose `src` was resolved straight to a signed CDN URL (`directUrls`) and so
 * carries no API URL for `rawAgentFileUrlFallback` to strip. Same target as
 * that fallback: the stored object via the API's redirect, streamed for SVG.
 *
 * Returns null when the ref is not an `ad:` ref or has no key in the map.
 */
export function rawAgentFileUrlForAdRef(
  adRef: string,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string,
): string | null {
  if (!adRef.startsWith("ad:") || !componentAssets) return null;
  const filename = adRef.slice("ad:".length);
  // Filenames are agent-supplied; bare indexing would hit Object.prototype.
  if (!Object.hasOwn(componentAssets, filename)) return null;
  const key = componentAssets[filename];
  if (!key) return null;
  const params = new URLSearchParams({ key });
  if (key.toLowerCase().endsWith(".svg")) {
    params.set("stream", "true");
  }
  return `${apiBase}/agents/files?${params.toString()}`;
}

export function parseAgentFileUrl(url: string): URL | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (!parsed.pathname.endsWith("/agents/files")) return null;
  if (!parsed.searchParams.has("key")) return null;
  return parsed;
}
